import { createStore } from 'zustand/vanilla'

import { getCommitSha } from '@gto/helpers/build-info'
import { generateAppUID, getCookie, setCrossDomainCookie } from '@gto/helpers/cookies'

// sessionStorage key to track if we've already attempted a version-based reload
const AUTH_RELOADED_KEY = 'gto:auth_reloaded'

// Auth failure event for notifications
export const AUTH_FAILURE_EVENT = 'gto:auth-failure' as const

export type AuthFailureDetail = {
  type: 'forced_logout' | 'session_expired'
}

export class AuthFailureEvent extends CustomEvent<AuthFailureDetail> {
  constructor(detail: AuthFailureDetail) {
    super(AUTH_FAILURE_EVENT, { detail })
  }
}

type SessionStore = {
  accessToken: string | null
  appUid: string
  refreshPromise: Promise<string> | null
  setAccessToken: (accessToken: string) => void
  clearAccessToken: () => void
  setRefreshPromise: (promise: Promise<string> | null) => void
  getRefreshPromise: () => Promise<string> | null
}

const appUidCookieName = 'GTO_APP_UID'
const getAppUID = () => {
  const appUid = getCookie(appUidCookieName)
  if (appUid && appUid.length > 0) {
    return appUid
  }

  const newAppUid = generateAppUID()
  setCrossDomainCookie(appUidCookieName, newAppUid, 1095)

  return newAppUid
}

export const sessionStore = createStore<SessionStore>((set, get) => ({
  accessToken: null,
  appUid: getAppUID(),
  refreshPromise: null,
  setAccessToken: (accessToken: string) => set({ accessToken }),
  clearAccessToken: () => set({ accessToken: null }),
  setRefreshPromise: (promise: Promise<string> | null) => set({ refreshPromise: promise }),
  getRefreshPromise: () => get().refreshPromise,
}))

const LOGIN_PATH = '/login'
const SHARED_HAND_PATH = '/shared-hand'
const PUBLIC_SPOT_OF_THE_WEEK_PATH = '/sotw'

// True when the browser is already sitting on the login page or any login
// sub-route (e.g. a future `/login/oauth` callback). This is intentionally
// stricter than gto-wizard-web-vue's `startsWith('/login')` — it won't match unrelated
// `/login-*` paths. Trailing slashes are normalised so `/login` and `/login/`
// both match; query params (e.g. the `?code=` OAuth callback) live outside
// `pathname`, so they don't affect this.
const isOnLoginPage = (): boolean => {
  const pathname = window.location.pathname.replace(/\/+$/, '')

  return pathname === LOGIN_PATH || pathname.startsWith(`${LOGIN_PATH}/`)
}

const isOnAnonymousPage = (): boolean => {
  const pathname = window.location.pathname.replace(/\/+$/, '')

  return (
    pathname === SHARED_HAND_PATH ||
    pathname.startsWith(`${SHARED_HAND_PATH}/`) ||
    pathname === PUBLIC_SPOT_OF_THE_WEEK_PATH
  )
}

const hasCredentialToLose = (): boolean => {
  if (sessionStore.getState().accessToken !== null) {
    return true
  }

  try {
    return localStorage.getItem('user_refresh') !== null
  } catch {
    return false
  }
}

let pendingLoginRedirect: ReturnType<typeof setTimeout> | undefined

/**
 * Cancels a queued post-auth-failure redirect to `/login`, if one is in flight.
 *
 * A test seam, and the reason it exists is specific: the redirect below is
 * deliberately deferred by 100ms, and under Vitest's `isolate: false` the app's
 * test files share one module registry and one jsdom. A 401 provoked by one
 * file therefore lands its `window.location.href = '/login'` inside WHICHEVER
 * file happens to be running 100ms later — silently repointing that file's
 * location out from under it. Cancelling between tests bounds the timer to the
 * test that caused it.
 *
 * Not for production use: nothing in the app should want to suppress a redirect
 * it just triggered.
 */
export const cancelPendingLoginRedirect = () => {
  if (pendingLoginRedirect === undefined) {
    return
  }
  clearTimeout(pendingLoginRedirect)
  pendingLoginRedirect = undefined
}

const dispatchAuthFailure = (isForcedLogout: boolean) => {
  if (isOnAnonymousPage() && !hasCredentialToLose()) {
    return
  }

  const detail: AuthFailureDetail = isForcedLogout
    ? {
        type: 'forced_logout',
      }
    : {
        type: 'session_expired',
      }

  // Clear solution preload detection flag on auth failure (see FE-133)
  sessionStorage.removeItem('_xs_9k2')

  window.dispatchEvent(new AuthFailureEvent(detail))

  // Redirecting to /login while ALREADY on /login is a full page reload that
  // re-mounts the app and re-fires the proactive token refresh on bootstrap.
  // When that refresh keeps failing — e.g. a logged-out session with no
  // `user_refresh`, so `/v1/token/refresh/` is sent with an empty body and the
  // backend returns 400 — the redirect + reload becomes an infinite loop (the
  // "login page keeps refreshing" symptom). The auth-failure notification has
  // already been dispatched above, so on the login page there is nothing left
  // to navigate to: skip the reload and let the page settle.
  if (isOnLoginPage()) {
    return
  }

  // Small delay to allow the notification to be saved before navigation
  pendingLoginRedirect = setTimeout(() => {
    pendingLoginRedirect = undefined
    if (hasLiveDocument()) {
      window.location.href = '/login'
    }
  }, 100)
}

type RefreshResponse = {
  access: string
}

export type RefreshRequestFn = (
  path: string,
  body: { refresh: string | undefined },
) => Promise<RefreshResponse>

// Check if error response indicates a forced logout (e.g., from AxiosError)
const isForcedLogoutError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null) {
    return false
  }

  // Check for AxiosError shape: error.response.data.code
  const errorObj = error as Record<string, unknown>
  const response = errorObj.response as Record<string, unknown> | undefined
  const data = response?.data as Record<string, unknown> | undefined

  return data?.code === 'FORCED_LOGOUT'
}

const hasAlreadyReloaded = (): boolean => {
  return sessionStorage.getItem(AUTH_RELOADED_KEY) === 'true'
}

const markAsReloaded = () => {
  sessionStorage.setItem(AUTH_RELOADED_KEY, 'true')
}

const clearReloadedFlag = () => {
  sessionStorage.removeItem(AUTH_RELOADED_KEY)
}

type VersionInfo = { hash: string }

// Get current client build hash: the page's commit SHA, else the React remote's own (app.tsx sets it)
const getClientBuildHash = (): string | undefined => {
  const reactSha: unknown = Reflect.get(window, 'GW_REACT_COMMIT_SHA')

  return getCommitSha() ?? (typeof reactSha === 'string' ? reactSha : undefined)
}

/**
 * Check if a newer version of the app is available on the server.
 * Fetches version.json with no-cache to get the latest server version.
 */
const checkForNewerVersion = async (): Promise<boolean> => {
  const clientHash = getClientBuildHash()

  // Can't check if no client hash available
  if (clientHash === undefined || clientHash === '') {
    return false
  }

  try {
    const response = await fetch('/version.json', {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    })

    if (!response.ok) {
      return false
    }

    const serverVersion = (await response.json()) as VersionInfo

    return serverVersion.hash !== clientHash
  } catch {
    // Can't check version (network error, etc.) - don't reload
    return false
  }
}

// Async refresh work can outlive its browser document. The required suite isolates
// files; nightly stress and isolation-contract runs also exercise shared workers.
const hasLiveDocument = (): boolean => typeof window !== 'undefined'

// Handle refresh failure: reload if newer version available, otherwise auth failure.
const handleRefreshFailure = async (error: unknown, rejectPromise: (reason: unknown) => void) => {
  if (!hasLiveDocument()) {
    rejectPromise(error)

    return
  }

  if (isForcedLogoutError(error)) {
    dispatchAuthFailure(true)
    rejectPromise(error)

    return
  }

  // If we haven't tried reloading yet, check for newer version
  const shouldReload = !hasAlreadyReloaded() && (await checkForNewerVersion())
  if (!hasLiveDocument()) {
    rejectPromise(error)

    return
  }

  if (shouldReload) {
    markAsReloaded()
    sessionStore.getState().setRefreshPromise(null)
    window.location.reload()

    return
  }

  dispatchAuthFailure(false)
  rejectPromise(error)
}

async function executeRefresh(
  refresh: string | undefined,
  makeRequest: RefreshRequestFn,
  resolvePromise: (value: string) => void,
  rejectPromise: (reason: unknown) => void,
): Promise<void> {
  const state = sessionStore.getState()

  try {
    const data = await makeRequest('/v1/token/refresh/', { refresh })
    state.setAccessToken(data.access)
    if (hasLiveDocument()) {
      clearReloadedFlag()
    }
    resolvePromise(data.access)
  } catch (error) {
    await handleRefreshFailure(error, rejectPromise)
  } finally {
    state.setRefreshPromise(null)
  }
}

export function refreshAccessToken(makeRequest: RefreshRequestFn): Promise<string> {
  const state = sessionStore.getState()

  // If a refresh is already in progress, wait for it
  const existingPromise = state.getRefreshPromise()
  if (existingPromise) {
    return existingPromise
  }

  // use refresh token from localStorage, otherwise BE tries to get it from HTTP only cookies
  const refresh = localStorage.getItem('user_refresh')

  // Use deferred promise pattern for atomic check-and-store semantics.
  // The promise is stored BEFORE any async work begins, preventing
  // concurrent calls from slipping through between check and store.
  let resolvePromise!: (value: string) => void
  let rejectPromise!: (reason: unknown) => void

  const promise = new Promise<string>((resolve, reject) => {
    resolvePromise = resolve
    rejectPromise = reject
  })

  // Store immediately - no async work has started yet
  state.setRefreshPromise(promise)

  // Execute async work and resolve/reject the deferred promise
  void executeRefresh(refresh ?? undefined, makeRequest, resolvePromise, rejectPromise)

  return promise
}
